import BlogPage from 'src/views/Marketing/Blog/components/BlogPage';
import Footer from 'src/views/Marketing/components/Footer';
import NavBar from 'src/views/Marketing/components/NavBar';
import { ScrollView, StyleSheet } from 'react-native';

export default function Page() {
  return (
    <ScrollView style={styles.scrollView}>
      <NavBar />
      <BlogPage
        author='OneShop Team'
        dateTime='2022-01-22'
        tag='Poshmark'
        title='A Crash Course on Crafting Captivating Poshmark Titles'
      >
        <p>
          <em>
            In this article, we'll show you the anatomy of a great listing title, plus more tips to help you create
            winning titles every time.
          </em>{' '}
        </p>

        <p>
          We know from the success of the microblogging site Twitter and social media platform Instagram that a short
          message could deliver much impact. Having limited characters allows the user to opt for well-chosen words to
          distill his thoughts in a potent nugget that his intended audience could easily digest and appreciate. The
          same goes for a Poshmark seller hoping to attract as many potential buyers using a title with less than 80
          characters.{' '}
        </p>
        <p>
          Excellent titles, along with good photos for your listing covers, are the gateway to purchases. It practically
          doesn't matter how much time and effort you've poured into thrifting and curating your available listings if
          you have terrible titles because probably only a few would click and discover them anyway.
        </p>
        <p>
          Titles, descriptions, photos, and customer service are all part of your package and branding as a seller. It
          is something you'd want old and new Poshers alike to associate with your Poshmark closet regardless of the
          category. For this piece, we'll be zeroing in on creating title templates that grab attention and, more
          importantly, and hopefully, lead to sales!
        </p>
        <p>
          A great Poshmark listing title should have most of the information in the checklist below. Depending on the
          item you're listing and your remaining character count, determine for yourself which details to retain or
          leave out – or bump off to the space allotted for description.{' '}
        </p>

        <p>
          <strong>Brand name.</strong> Like any other content on the internet, Poshmark enables search engine
          optimization. Without SEO, the listings would be a huge virtual mess, and buyers would have difficulty finding
          what they're looking for. That's why a seller should always maximize the space with precious keywords,
          starting with the brand name. Some would type it in all caps or lowercase. We think, though, that sticking
          with how the brand is written always looks more professional and polished (e.g., Kate Spade, ACME, & Other
          Stories), even though this may not directly affect how your listings show up on searches.
        </p>
        <p>
          <strong>Style name.</strong> Some buyers look for very specific items, and you wouldn't want them to miss your
          listing just because you incorporated some generic adjectives instead of the item's official style name. This
          may not apply to all brands and items, but if you do have this information ready (or can easily be available
          through a quick Google search), please include it. For instance, Gucci Ophidia GG Small Shoulder Bag looks
          more professional than Gucci Striped Monogram Bag. If there's no official style name, aim for an informative
          description highlighting a design detail or material (e.g., <em>Ruffle-Trimmed Ponte Pencil Skirt</em>)
          instead of fluffy adjectives (e.g., <em>Stylish and Sexy Mini Skirt</em>).{' '}
        </p>
        <p>
          <strong>Size.</strong> It can be disheartening for any potential buyer to click on a listing for a pair of
          shoes or a dress that turns out to be a couple of sizes too big or small. Being upfront about the size by
          including it in the title lets it easily be seen by those who fit its size (and save those who purchase them
          from disappointment.)
        </p>
        <p>
          <strong>Color and/or material.</strong> Photos can be misleading, so it's always advisable to take photos of
          your items with natural light to better depict color and texture. Using a hanger or mannequin for "when worn"
          photos, including lay-flat photo options, and avoiding shadows when photographing also helps. However,
          potential buyers won't see this until they click on your listing. That's why it's a good idea to include the
          garment's material and color in your title, especially if the color of the item can be easily mistaken for
          another (e.g., midnight blue for black, or teal for green.){' '}
        </p>
        <p>
          <strong>Usage condition.</strong> The majority of the items on reselling platforms are gently used. If your
          listings are otherwise new with or without tags (NWT / NWOT) or in excellent used condition (EUC), you might
          want to include that in the title to instantly grab potential buyers' attention looking specifically for such.{' '}
        </p>
        <p>
          <strong>Any outstanding/unique feature.</strong> Now, this is a piece of information that you must incorporate
          with caution. Don't put "Rare," "Vintage," or "Limited Edition" unless you are certain and have proof. Also,
          refrain from putting "Genuine" or "100% Authentic" (coupled with emojis and other weird characters) because,
          to be honest, those can ironically make your listing look un-genuine and in-authentic.{' '}
        </p>
        <p>
          <strong>No typos, misspellings, and distracting characters.</strong> It is simultaneously appalling and
          amusing to find titles that didn't undergo thoughtful crafting and proofreading – otherwise, the likes of Tore
          Burch, Stella McCarthy, and Channel wouldn't exist. Let another pair of eyes check your listing before posting
          it. Or if there's no one to proofread for you, rest your eyes for at least half an hour before checking the
          listing again with fresher vision.{' '}
        </p>
        <p>
          You should also steer clear from cutesy emojis and weird characters that don't do anything to your listing
          except to annoy potential buyers. Incorporating a few relevant and well-placed characters, such as the spade
          symbol in a Kate Spade listing title to make it more attractive, is okay, but perhaps not the best use of the
          limited space.
        </p>
        <p>
          <strong>Choose a format and use it across all of your listings.</strong> Consider using all-caps to highlight
          only a certain word or words in a title, but don't use it for the entire title unless you want to come across
          as mad and screaming. Don't use all lowercase either, as it will make your title look like it's written by
          someone amateur and lazy. To add "texture" to your title and avoid looking like a dull, monotonous string of
          words, incorporate hyphens and parenthesis. Once you've figured out the format that works for you, stick with
          it to make your listings look cohesive – yes, it's still part of your branding!{' '}
        </p>

        <p>Here are some inspirations for your title template: </p>
        <ul>
          <li>Balenciaga Hourglass XS Top Handle Python-Embossed Leather (Purple)</li>
          <li>[NWT] Christian Louboutin Lady Peep Spikes Swarovski Heels Black – Size 10</li>
          <li>3.1 Phillip Lim x Target | Floral Sleeveless Peplum Top Size M</li>
        </ul>

        <p>
          Running a store in virtual marketplaces, either a side hustle or a full-time business, requires plenty of time
          and effort. Aside from finding actual items to sell, taking photos, listing each one with a great title and
          description, and preparing shipping labels, Poshmark sellers also need to think of strategies for promotions
          and discounts. They also need to allot time and energy in marketing efforts such as sharing listings (both
          self-shares and community shares) and growing their followers and likers – tasks OneShop could easily manage.
          Start our 7-day free trial today to find out how it can improve your reselling business!
        </p>
      </BlogPage>
      <Footer />
    </ScrollView>
  );
}

const styles = StyleSheet.create({
  scrollView: {
    flex: 1,
  },
});
